/* eslint-disable @typescript-eslint/no-explicit-any */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import { Collapse, Nav } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import classNames from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { Route } from 'sitemap';
import { capitalize } from 'helpers/utils';
import { useNavbarVerticalCollapse } from './NavbarVerticalCollapseProvider';
import Badge from 'components/base/Badge';
import { useAppContext } from 'providers/AppProvider';
import customerEntityService from 'service/charterer';

interface NavbarVerticalMenuProps {
  routes: Route[];
  level: number;
}

interface NavItemProps {
  route: Route;
  level: number;
  dataCounts: any;
  dataApplications: any;
}

const NavItem = ({
  route,
  level,
  dataCounts,
  dataApplications
}: NavItemProps) => {
  const {
    config: { isNavbarVerticalCollapsed }
  } = useAppContext();
  const { setOpenItems, openItems } = useNavbarVerticalCollapse();
  const { t } = useTranslation();
  return (
    <Nav.Item as="li">
      <NavLink
        /* prettier-ignore */
        to={route.path ? route.path : '#!'}
        className={({ isActive }) =>
          classNames('nav-link', {
            'label-1': level === 1,
            active: isActive && route.path !== '#!'
          })
        }
        onClick={() => level === 1 && setOpenItems(openItems.map(() => ''))}
      >
        <div
          className={classNames('d-flex', {
            'text-300': !route.active
          })}
        >
          {route.icon ? (
            <>
              <span
                className={classNames('nav-link-icon', {
                  new: route.new || route.hasNew
                })}
              >
                {route.iconSet === 'font-awesome' ? (
                  <FontAwesomeIcon
                    icon={route.icon as IconProp}
                    transform={{ size: 16 }}
                  />
                ) : (
                  <FeatherIcon icon={route.icon} size={16} />
                )}
              </span>
              <span className="nav-link-text-wrapper">
                <span className="nav-link-text">
                  {capitalize(t(route.name))}
                </span>
                {route.new && !isNavbarVerticalCollapsed && (
                  <Badge variant="phoenix" bg="info" className="ms-2">
                    New
                  </Badge>
                )}
              </span>
            </>
          ) : (
            <>
              <span className="nav-link-text" style={{ paddingBottom: '2px' }}>
                {capitalize(t(route.name))}
                {route.new && (
                  <Badge variant="phoenix" bg="info" className="ms-2">
                    New
                  </Badge>
                )}
                {route.path === '/dtkt/provider-applications' &&
                  dataCounts?.creditor_count && (
                    <Badge variant="phoenix" bg="danger" className="ms-2">
                      {dataCounts?.creditor_count || 0}
                    </Badge>
                  )}
                {route.path === '/dtkt/charterer-applications' &&
                  dataCounts?.debitor_count && (
                    <Badge variant="phoenix" bg="danger" className="ms-2">
                      {dataCounts?.debitor_count || 0}
                    </Badge>
                  )}
                {route.path === '/applications/outgoing' &&
                  dataApplications?.out_count && (
                    <Badge
                      variant="phoenix"
                      bg="danger-animate"
                      className="ms-2"
                    >
                      {dataApplications?.out_count || 0}
                    </Badge>
                  )}
                {route.path === '/applications/coming' &&
                  dataApplications?.in_count && (
                    <Badge
                      variant="phoenix"
                      bg="danger-animate"
                      className="ms-2"
                    >
                      {dataApplications?.in_count || 0}
                    </Badge>
                  )}
              </span>
            </>
          )}
        </div>
      </NavLink>
    </Nav.Item>
  );
};

const CollapsableNavItem = ({
  route,
  level,
  dataCounts,
  dataApplications
}: NavItemProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { setOpenItems, openItems } = useNavbarVerticalCollapse();

  const {
    config: { isNavbarVerticalCollapsed }
  } = useAppContext();

  const openCollapse = (childrens: Route[] = []) => {
    const checkLink = (children: Route) => {
      if (`${children.path}` === pathname) {
        return true;
      }
      return children.pages && children.pages.some(checkLink);
    };
    return childrens.some(checkLink);
  };

  const updateOpenItems = (name: string) => {
    const updatedOpenItems = [...openItems];
    updatedOpenItems[level] = name;
    updatedOpenItems.forEach((item, index) => {
      if (index > level) {
        updatedOpenItems[index] = '';
      }
    });
    setOpenItems(updatedOpenItems);
  };

  useEffect(() => {
    if (openCollapse(route.pages)) {
      updateOpenItems(route.name);
    }
  }, []);

  return (
    <>
      <Nav.Link
        onClick={() => {
          if (route.name === openItems[level]) {
            updateOpenItems('');
          } else {
            updateOpenItems(route.name);
          }
        }}
        className={classNames('dropdown-indicator', {
          'label-1': level === 1,
          collapsed: openItems[level] !== route.name,
          'text-300': !route.active
        })}
        aria-expanded={openItems[level] === route.name}
      >
        <div className="d-flex align-items-center">
          <div className="dropdown-indicator-icon">
            <FontAwesomeIcon
              icon={faCaretRight}
              className={classNames({
                'text-300': !route.active
              })}
            />
          </div>
          {level === 1 && (
            <span
              className={classNames('nav-link-icon', {
                new: route.new || route.hasNew
              })}
            >
              {route.iconSet === 'font-awesome' ? (
                <FontAwesomeIcon
                  icon={route.icon as IconProp}
                  transform={{ size: 16 }}
                />
              ) : (
                <FeatherIcon icon={route.icon} size={16} />
              )}
              {/* <FeatherIcon icon={route.icon} size={16} /> */}
            </span>
          )}
          { /* prettier-ignore */}
          <span
            className={classNames('nav-link-text', {
              new: route.hasNew
            })}
            style={{ paddingBottom: '2px' }}
          >
            {capitalize(t(route.name))}
            {(!isNavbarVerticalCollapsed || level !== 1) && (
              ['outgoing', 'incoming', 'reviews'].includes(route.name) ? (
                (() => {
                  const count =
                    route.name === 'outgoing'
                      ? dataApplications?.out_count
                      : dataApplications?.in_count;

                  return count > 0 ? (
                    <Badge
                      variant="phoenix"
                      bg="danger-animate"
                      className="ms-2"
                    >
                      {count}
                    </Badge>
                  ) : null;
                })()
              ) : (
                (['supplier', 'customer', 'applications', 'dt_kt'].includes(route.name) &&
                  openItems[level] !== route.name) && (() => {
                    const count =
                      route.name === 'supplier'
                        ? dataCounts?.creditor_count
                        : route.name === 'customer'
                          ? dataCounts?.debitor_count
                          : route.name === 'applications'
                            ? dataApplications?.total_count
                            : route.name === 'dt_kt'
                              ? dataCounts?.total_count
                              : 0;

                    return count > 0 ? (
                      <Badge
                        variant="phoenix"
                        bg={
                          route.name === 'dt_kt' || route.name === 'internet_store'
                            ? 'danger'
                            : route.name === 'supplier' || route.name === 'customer'
                              ? 'danger'
                              : 'danger-animate'
                        }
                        className="ms-2"
                      >
                        {count}
                      </Badge>
                    ) : null;
                  })()
              )
            )}

          </span>
        </div>
      </Nav.Link>
      <div
        className={classNames('parent-wrapper', {
          'label-1': level === 1
        })}
      >
        <Collapse in={openItems[level] === route.name} className="nav parent">
          <div>
            {level === 1 && (
              <div className="collapsed-nav-item-title d-none">
                {capitalize(t(route.name))}
                {isNavbarVerticalCollapsed && route.new && (
                  <Badge variant="phoenix" bg="info" className="ms-2">
                    New
                  </Badge>
                )}
              </div>
            )}
            <NavbarVerticalMenu routes={route.pages || []} level={level + 1} />
          </div>
        </Collapse>
      </div>
    </>
  );
};
const NavbarVerticalMenu = ({ routes, level }: NavbarVerticalMenuProps) => {
  const userId = useSelector((state: any) => state?.auth?.user?.id);

  const userPermissions = useSelector(
    (state: any) => state?.auth?.user_permissions
  );

  const { data: dataCounts } = useQuery(
    ['GET_CUSTOMER_ENTITY_PLAN', userId],
    async () => {
      if (userId) {
        const res = await customerEntityService.getListCountPlan({});

        return res;
      }
    }
  );
  const { data: dataApplications } = useQuery(
    ['GET_CUSTOMER_ENTITY_APPLICATION', userId],
    async () => {
      if (userId) {
        const res = await customerEntityService.getListApplications({});

        return res;
      }
    }
  );

  const hasPermission = (route: Route): boolean => {
    if (route.path === '/cash-sale') {
      return true;
    }

    const hasMenuPidPermission = route.menu_pid
      ? userPermissions?.some((perm: any) => perm.menu_pid === route.menu_pid)
      : false;

    if (route.pages && route.pages.length > 0) {
      return route.pages.some(child => hasPermission(child));
    }

    return hasMenuPidPermission;
  };

  const filterChildRoutes = (routes: any): Route[] => {
    return routes.map((route: any) => {
      if (route.pages) {
        const filteredPages = filterChildRoutes(
          route.pages.filter((child: any) => hasPermission(child))
        );
        return {
          ...route,
          pages: filteredPages.length > 0 ? filteredPages : undefined
        };
      }
      return route;
    });
  };

  const filteredRoutes = routes?.filter((route: Route) => {
    if (!hasPermission(route)) {
      return false;
    }

    return true;
  });

  const processedRoutes = filterChildRoutes(filteredRoutes || []);

  return (
    <>
      {processedRoutes.map((route: any) => (
        <div key={route.name}>
          {level === 1 ? (
            <div className="nav-item-wrapper">
              {route.pages ? (
                <CollapsableNavItem
                  route={route}
                  level={level}
                  dataCounts={dataCounts}
                  dataApplications={dataApplications}
                />
              ) : (
                <NavItem
                  route={route}
                  level={level}
                  dataCounts={dataCounts}
                  dataApplications={dataApplications}
                />
              )}
            </div>
          ) : (
            <>
              {route.pages ? (
                <CollapsableNavItem
                  route={route}
                  level={level}
                  dataCounts={dataCounts}
                  dataApplications={dataApplications}
                />
              ) : (
                <NavItem
                  route={route}
                  level={level}
                  dataCounts={dataCounts}
                  dataApplications={dataApplications}
                />
              )}
            </>
          )}
        </div>
      ))}
    </>
  );
};

export default NavbarVerticalMenu;
